import { useEffect, useRef, useState } from 'react'
import { useAdminApi } from '../api'
import { t, DictKey } from '../i18n'
import { useLang } from '../LangContext'
import { formatDate, localeFor } from '../../utils/format'
import editStyles from '../adminEdit.module.css'
import { Icon } from '../../styles/Icon'

interface Bloque {
  id: string
  tipo: string
  fecha_activacion: string
}

interface Festivo {
  id: string
  fecha: string
  descripcion?: string
}

export function TemporadaDetail({
  temporadaId,
  nombre,
  onClose,
}: {
  temporadaId: string
  nombre: string
  onClose: () => void
}) {
  const api = useAdminApi()
  const lang = useLang()
  const locale = localeFor(lang)
  const panelRef = useRef<HTMLElement>(null)
  const [bloques, setBloques] = useState<Bloque[]>([])
  const [festivos, setFestivos] = useState<Festivo[]>([])
  const [bloqueForm, setBloqueForm] = useState({ tipo: 'unico', fecha_activacion: '' })
  const [festivoForm, setFestivoForm] = useState({ fecha: '', descripcion: '' })

  async function refresh() {
    setBloques(await api.get(`/admin/temporadas/${temporadaId}/bloques`))
    setFestivos(await api.get(`/admin/temporadas/${temporadaId}/festivos`))
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaId])

  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [temporadaId])

  async function handleCreateBloque(e: React.FormEvent) {
    e.preventDefault()
    await api.post(`/admin/temporadas/${temporadaId}/bloques`, bloqueForm)
    setBloqueForm({ tipo: 'unico', fecha_activacion: '' })
    await refresh()
  }

  async function handleCreateFestivo(e: React.FormEvent) {
    e.preventDefault()
    await api.post(`/admin/temporadas/${temporadaId}/festivos`, festivoForm)
    setFestivoForm({ fecha: '', descripcion: '' })
    await refresh()
  }

  async function handleDeleteFestivo(id: string) {
    await api.del(`/admin/temporadas/${temporadaId}/festivos/${id}`)
    await refresh()
  }

  return (
    <section ref={panelRef} className={editStyles.panel} aria-label={`${t('editando', lang)}: ${nombre}`}>
      <div className={editStyles.panelHeader}>
        <h3 className={editStyles.panelTitle}>
          {t('editando', lang)}: {nombre}
        </h3>
        <button onClick={onClose}><Icon name="cancelar" />{t('cancelar', lang)}</button>
      </div>

      <h4>{t('bloques', lang)}</h4>
      <ul>
        {bloques.map((bloque) => (
          <li key={bloque.id}>
            {t(`bloque_${bloque.tipo}` as DictKey, lang)} — {formatDate(bloque.fecha_activacion, locale)}
          </li>
        ))}
      </ul>
      <form onSubmit={handleCreateBloque}>
        <select
          value={bloqueForm.tipo}
          onChange={(e) => setBloqueForm({ ...bloqueForm, tipo: e.target.value })}
        >
          {(['pretemporada', 'temporada', 'unico'] as const).map((tipo) => (
            <option key={tipo} value={tipo}>
              {t(`bloque_${tipo}`, lang)}
            </option>
          ))}
        </select>
        <input
          type="date"
          required
          value={bloqueForm.fecha_activacion}
          onChange={(e) => setBloqueForm({ ...bloqueForm, fecha_activacion: e.target.value })}
        />
        <button type="submit"><Icon name="crear" />{t('crear', lang)}</button>
      </form>

      <h4>{t('festivos', lang)}</h4>
      <ul>
        {festivos.map((festivo) => (
          <li key={festivo.id}>
            {formatDate(festivo.fecha, locale)} {festivo.descripcion}
            <button onClick={() => handleDeleteFestivo(festivo.id)}><Icon name="eliminar" /><span className="sr-only">{t('eliminar', lang)}</span></button>
          </li>
        ))}
      </ul>
      <form onSubmit={handleCreateFestivo}>
        <input
          type="date"
          required
          value={festivoForm.fecha}
          onChange={(e) => setFestivoForm({ ...festivoForm, fecha: e.target.value })}
        />
        <input
          placeholder={t('descripcion', lang)}
          value={festivoForm.descripcion}
          onChange={(e) => setFestivoForm({ ...festivoForm, descripcion: e.target.value })}
        />
        <button type="submit"><Icon name="crear" />{t('crear', lang)}</button>
      </form>
    </section>
  )
}
